import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import posts from '../../data/posts';
import Stars from '../../components/Stars/Stars';
import LocationMap from '../../components/Map/LocationMap';
import LikeButton from '../../components/LikeButton/LikeButton';
import './PostPage.css';

const INTERVAL_MS = 8000;

function PostSlideshow({ images, title }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % images.length);
    }, INTERVAL_MS);
    return () => clearInterval(timer);
  }, [images.length]);

  const prev = () => setCurrent((c) => (c - 1 + images.length) % images.length);
  const next = () => setCurrent((c) => (c + 1) % images.length);

  return (
    <div className="post-slideshow">
      <div className="post-slideshow-track" style={{ transform: `translateX(-${current * 100}%)` }}>
        {images.map((src, i) => (
          <img key={i} src={src} alt={`${title} — ${i + 1}`} className="post-slideshow-img" />
        ))}
      </div>
      <button className="post-slideshow-btn post-slideshow-btn--prev" onClick={prev} aria-label="Previous image">&#8249;</button>
      <button className="post-slideshow-btn post-slideshow-btn--next" onClick={next} aria-label="Next image">&#8250;</button>
      <div className="post-slideshow-dots">
        {images.map((_, i) => (
          <button
            key={i}
            className={`post-slideshow-dot${i === current ? ' active' : ''}`}
            onClick={() => setCurrent(i)}
            aria-label={`Go to image ${i + 1}`}
          />
        ))}
      </div>
      <div className="post-slideshow-progress">
        <div key={current} className="post-slideshow-progress-bar" style={{ animationDuration: `${INTERVAL_MS}ms` }} />
      </div>
    </div>
  );
}

function PostPage() {
  const { slug } = useParams();
  const post = posts.find((p) => p.slug === slug);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | Luke's Breakfast Blog`;
    return () => { document.title = "Luke's Breakfast Blog"; };
  }, [post]);

  if (!post) {
    return (
      <main className="post-page">
        <p>Post not found. <Link to="/">← Back home</Link></p>
      </main>
    );
  }

  const images = post.images ?? [
    post.image,
    `https://placehold.co/800x400?text=${encodeURIComponent(post.title + ' 2')}`,
    `https://placehold.co/800x400?text=${encodeURIComponent(post.title + ' 3')}`,
  ];

  const index = posts.indexOf(post);
  const prevPost = posts[index - 1] ?? null;
  const nextPost = posts[index + 1] ?? null;

  return (
    <main className="post-page">
      <Link to="/" className="back-link">← Back</Link>
      <PostSlideshow images={images} title={post.title} />
      {(post.rating || post.cost != null) && (
        <div className="post-rating">
          {post.rating && <Stars rating={post.rating} />}
          {post.cost != null && <span className="post-cost">£{post.cost.toFixed(2)}</span>}
        </div>
      )}
      <div className="post-meta">
        <p className="post-date">{post.date}</p>
        <span className="post-read-time">2 min read</span>
      </div>
      <h1 className="post-title">{post.title}</h1>
      {post.tags?.length > 0 && (
        <div className="post-tags">
          {post.tags.map((tag) => (
            <span key={tag} className="post-tag">{tag}</span>
          ))}
        </div>
      )}
      <div className="post-body">
        {post.body.map((item, i) =>
          typeof item === 'string' ? (
            <p key={i}>{item}</p>
          ) : (
            <img
              key={i}
              src={item.src}
              alt={item.alt}
              className={`post-body-img post-body-img--${item.float}`}
            />
          )
        )}
      </div>

      {post.links?.length > 0 && (
        <div className="post-links-bar">
          {post.links.map((link) => (
            <a key={link.label} className="post-link-btn" href={link.href} target="_blank" rel="noopener noreferrer">
              {link.label}
            </a>
          ))}
        </div>
      )}

      <div className="post-share-bar">
        <LikeButton slug={post.slug} initialLikes={post.likes ?? 0} />
        <a
          className="post-share-btn post-share-btn--twitter"
          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`"${post.title}" — ${post.blurb}`)}&url=${encodeURIComponent(window.location.href)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg className="post-share-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.736-8.857L2.25 2.25h6.865l4.255 5.626 4.874-5.626Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
          </svg>
          Share this review
        </a>
        <a className="post-share-btn post-share-btn--rss" href="/feed.xml" target="_blank" rel="noopener noreferrer">
          <svg className="post-share-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M6.18 15.64a2.18 2.18 0 0 1 2.18 2.18C8.36 19.01 7.38 20 6.18 20C4.98 20 4 19.01 4 17.82a2.18 2.18 0 0 1 2.18-2.18M4 4.44A15.56 15.56 0 0 1 19.56 20h-2.83A12.73 12.73 0 0 0 4 7.27V4.44m0 5.66a9.9 9.9 0 0 1 9.9 9.9h-2.83A7.07 7.07 0 0 0 4 12.93V10.1z" />
          </svg>
          Subscribe via RSS
        </a>
      </div>

      {post.location && (
        <div className="post-map-section">
          <h2 className="post-map-heading">Location</h2>
          <p className="post-map-location">📍 {post.location}</p>
          <LocationMap location={post.location} />
        </div>
      )}

      <nav className="post-pagination">
        {prevPost ? (
          <Link to={`/post/${prevPost.slug}`} className="post-pagination-link post-pagination-link--prev">
            <span className="post-pagination-label">← Previous</span>
            <span className="post-pagination-title">{prevPost.title}</span>
          </Link>
        ) : <span />}
        {nextPost && (
          <Link to={`/post/${nextPost.slug}`} className="post-pagination-link post-pagination-link--next">
            <span className="post-pagination-label">Next →</span>
            <span className="post-pagination-title">{nextPost.title}</span>
          </Link>
        )}
      </nav>
    </main>
  );
}

export default PostPage;
